export interface Testimonial {
  id: string;
  name: string;
  location: string;
  age?: number;
  memberSince?: string;
  quote: string;
  shortQuote?: string;
  context: 'community' | 'fasting' | 'carnivore' | 'transformation' | 'support';
  avatar?: string; // placeholder path for future photos
  featured?: boolean;
}

export const testimonials: Testimonial[] = [
  // COMMUNITY / BELONGING FOCUSED
  {
    id: 'michelle-t',
    name: 'Michelle T.',
    location: 'Austin, TX',
    age: 42,
    memberSince: '2023',
    quote: "I finally don't feel like a weirdo. For years, I ate differently than everyone around me and felt like I had to constantly explain myself. These people get it. They're my people. I've never felt so understood.",
    shortQuote: "I finally don't feel like a weirdo. These people get it.",
    context: 'community',
    featured: true
  },
  {
    id: 'david-k',
    name: 'David K.',
    location: 'Denver, CO',
    age: 38,
    memberSince: '2022',
    quote: "The community is why I'm still doing this. Without them, I would have quit months ago. When you have people cheering you on who actually understand what you're going through, everything changes.",
    shortQuote: "The community is why I'm still doing this.",
    context: 'community',
    featured: true
  },
  {
    id: 'jennifer-r',
    name: 'Jennifer R.',
    location: 'Seattle, WA',
    age: 35,
    memberSince: '2023',
    quote: "Someone answered my question at 3am. That's when I knew I'd found my tribe. I was struggling, couldn't sleep, and within minutes someone was there helping me through it. That kind of support doesn't exist anywhere else.",
    shortQuote: "Someone answered my question at 3am. That's when I knew I'd found my tribe.",
    context: 'support',
    featured: true
  },
  {
    id: 'marcus-j',
    name: 'Marcus J.',
    location: 'Chicago, IL',
    age: 45,
    memberSince: '2022',
    quote: "My wife thinks I'm crazy. My coworkers think I'm crazy. But in this community? I'm just one of the gang. It's the first time in years I haven't felt like I need to defend how I eat.",
    shortQuote: "In this community, I'm just one of the gang.",
    context: 'community',
    featured: true
  },
  {
    id: 'sarah-m',
    name: 'Sarah M.',
    location: 'Phoenix, AZ',
    age: 29,
    memberSince: '2023',
    quote: "I tried doing this alone for six months. I kept falling off. The day I joined this community, everything changed. Having people to check in with, to celebrate wins with, to get advice from—it makes all the difference.",
    shortQuote: "I tried doing this alone for six months. The day I joined, everything changed.",
    context: 'community',
    featured: true
  },
  {
    id: 'robert-h',
    name: 'Robert H.',
    location: 'Miami, FL',
    age: 52,
    memberSince: '2022',
    quote: "At 52, I thought I was too old to find 'my people.' Wrong. This community has members from 25 to 70+. Age doesn't matter. What matters is we all get each other.",
    shortQuote: "At 52, I thought I was too old to find my people. Wrong.",
    context: 'community'
  },
  {
    id: 'amanda-l',
    name: 'Amanda L.',
    location: 'Portland, OR',
    age: 33,
    memberSince: '2023',
    quote: "The loneliness was the hardest part. Not the food, not the fasting—the feeling that nobody understood. Finding this community felt like coming home.",
    shortQuote: "Finding this community felt like coming home.",
    context: 'community',
    featured: true
  },
  {
    id: 'james-w',
    name: 'James W.',
    location: 'Nashville, TN',
    age: 41,
    memberSince: '2022',
    quote: "I've been in other online communities before. They're usually dead or full of drama. This one is different. People actually show up for each other. Every single day.",
    shortQuote: "People actually show up for each other. Every single day.",
    context: 'community'
  },

  // FASTING FOCUSED
  {
    id: 'lisa-c',
    name: 'Lisa C.',
    location: 'San Diego, CA',
    age: 47,
    memberSince: '2023',
    quote: "Fasting used to feel like torture. Now it's just... easy. The nano approach changed everything for me. I went from barely managing 12 hours to comfortably doing 20+ without even thinking about it.",
    shortQuote: "Fasting used to feel like torture. Now it's just easy.",
    context: 'fasting',
    featured: true
  },
  {
    id: 'mike-b',
    name: 'Mike B.',
    location: 'Atlanta, GA',
    age: 36,
    memberSince: '2022',
    quote: "I'd tried every fasting protocol out there. Always white-knuckling it. The gradual approach in Nano Fasting finally made it sustainable. No more fighting myself.",
    shortQuote: "The gradual approach finally made fasting sustainable.",
    context: 'fasting'
  },
  {
    id: 'karen-d',
    name: 'Karen D.',
    location: 'Boston, MA',
    age: 55,
    memberSince: '2023',
    quote: "The mental clarity I get from fasting is incredible. And having the community to share that with—people who understand what you mean when you say you feel 'clear'—that's priceless.",
    shortQuote: "The mental clarity from fasting is incredible.",
    context: 'fasting'
  },
  {
    id: 'tom-p',
    name: 'Tom P.',
    location: 'Houston, TX',
    age: 44,
    memberSince: '2022',
    quote: "What I love about nano fasting is there's no pressure. You progress at your own pace. Some weeks I push further, some weeks I hold steady. It's sustainable because it's flexible.",
    shortQuote: "Nano fasting is sustainable because it's flexible.",
    context: 'fasting'
  },

  // CARNIVORE FOCUSED
  {
    id: 'rachel-s',
    name: 'Rachel S.',
    location: 'Minneapolis, MN',
    age: 31,
    memberSince: '2023',
    quote: "Carnivore seemed extreme until I tried it. Now it's just how I eat. The simplicity is the best part—no more agonizing over what to have for dinner. Meat. Done.",
    shortQuote: "The simplicity is the best part. Meat. Done.",
    context: 'carnivore',
    featured: true
  },
  {
    id: 'brian-f',
    name: 'Brian F.',
    location: 'Dallas, TX',
    age: 39,
    memberSince: '2022',
    quote: "Everyone thought I was nuts when I started eating only meat. A year later, they've stopped asking questions. Results speak for themselves.",
    shortQuote: "Everyone thought I was nuts. Results speak for themselves.",
    context: 'carnivore'
  },
  {
    id: 'stephanie-n',
    name: 'Stephanie N.',
    location: 'San Francisco, CA',
    age: 28,
    memberSince: '2023',
    quote: "I was a vegetarian for 10 years before trying carnivore. I know that sounds crazy. But I was curious, I tried it, and I've never looked back. This community helped me through the transition.",
    shortQuote: "I was a vegetarian for 10 years. I tried carnivore and never looked back.",
    context: 'carnivore'
  },
  {
    id: 'chris-m',
    name: 'Chris M.',
    location: 'Philadelphia, PA',
    age: 34,
    memberSince: '2022',
    quote: "The food freedom is real. No more calorie counting, no more macro tracking, no more meal prep anxiety. I eat when I'm hungry, I eat meat, I stop when I'm full. That's it.",
    shortQuote: "No more calorie counting. I eat when I'm hungry, I eat meat. That's it.",
    context: 'carnivore'
  },

  // TRANSFORMATION / JOURNEY FOCUSED
  {
    id: 'patricia-g',
    name: 'Patricia G.',
    location: 'Orlando, FL',
    age: 48,
    memberSince: '2022',
    quote: "Six months ago I was skeptical about everything—carnivore, fasting, online communities. Now I can't imagine my life without any of them. Sometimes you just have to take the leap.",
    shortQuote: "Six months ago I was skeptical about everything. Now I can't imagine life without it.",
    context: 'transformation',
    featured: true
  },
  {
    id: 'kevin-r',
    name: 'Kevin R.',
    location: 'Las Vegas, NV',
    age: 37,
    memberSince: '2023',
    quote: "The first week was hard. The first month was an adjustment. But somewhere around month two, it just clicked. I stopped thinking about food all the time. I got my mental energy back.",
    shortQuote: "Somewhere around month two, it just clicked.",
    context: 'transformation'
  },
  {
    id: 'diane-w',
    name: 'Diane W.',
    location: 'Charlotte, NC',
    age: 61,
    memberSince: '2022',
    quote: "At 61, I thought my best years were behind me. Starting carnivore and finding this community proved me wrong. I have more energy and mental clarity than I did at 40.",
    shortQuote: "At 61, I have more energy and clarity than I did at 40.",
    context: 'transformation'
  },
  {
    id: 'alex-t',
    name: 'Alex T.',
    location: 'Detroit, MI',
    age: 26,
    memberSince: '2023',
    quote: "My friends think I'm in a cult. I just laugh. If wanting to feel good and having supportive people in my life is a cult, sign me up.",
    shortQuote: "My friends think I'm in a cult. I just laugh.",
    context: 'community'
  },

  // SUPPORT FOCUSED
  {
    id: 'nancy-h',
    name: 'Nancy H.',
    location: 'Kansas City, MO',
    age: 53,
    memberSince: '2022',
    quote: "I had a really rough week—family stress, work pressure, wanted to quit everything. I posted in the community and within an hour had 20 people in my corner. That's when I knew this was different.",
    shortQuote: "I posted about a rough week. Within an hour, 20 people were in my corner.",
    context: 'support',
    featured: true
  },
  {
    id: 'greg-l',
    name: 'Greg L.',
    location: 'Salt Lake City, UT',
    age: 43,
    memberSince: '2023',
    quote: "The 24/7 support is real. It doesn't matter what time zone you're in or what hour you're struggling—someone is always there. I've never experienced anything like it.",
    shortQuote: "It doesn't matter what hour you're struggling—someone is always there.",
    context: 'support'
  },
  {
    id: 'maria-v',
    name: 'Maria V.',
    location: 'Tampa, FL',
    age: 38,
    memberSince: '2022',
    quote: "English is my second language and I was nervous about joining. But everyone was so welcoming. They helped me understand things, answered my questions patiently. This community is genuinely kind.",
    shortQuote: "Everyone was so welcoming. This community is genuinely kind.",
    context: 'support'
  },
  {
    id: 'steve-c',
    name: 'Steve C.',
    location: 'Raleigh, NC',
    age: 49,
    memberSince: '2023',
    quote: "I've been doing carnivore for 2 years on my own before joining. Wish I'd found this community sooner. Having people to share the journey with makes it so much better.",
    shortQuote: "Wish I'd found this community sooner.",
    context: 'community'
  },
  {
    id: 'linda-b',
    name: 'Linda B.',
    location: 'Columbus, OH',
    age: 57,
    memberSince: '2022',
    quote: "The $97 was the best money I ever spent. Lifetime access to a community of people who get it? That's worth more than any course or program I've ever bought.",
    shortQuote: "$97 was the best money I ever spent. Lifetime access to people who get it.",
    context: 'community',
    featured: true
  }
];

// Helper functions to filter testimonials
export const getFeaturedTestimonials = () =>
  testimonials.filter(t => t.featured);

export const getTestimonialsByContext = (context: Testimonial['context']) =>
  testimonials.filter(t => t.context === context);

export const getCommunityTestimonials = () =>
  testimonials.filter(t => t.context === 'community' || t.context === 'support');

export const getFastingTestimonials = () =>
  testimonials.filter(t => t.context === 'fasting');

export const getCarnivoreTestimonials = () =>
  testimonials.filter(t => t.context === 'carnivore');

export const getTransformationTestimonials = () =>
  testimonials.filter(t => t.context === 'transformation');

// Get random testimonials for variety
export const getRandomTestimonials = (count: number) => {
  const shuffled = [...testimonials].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};
